// Renders a ```netwatch-findings block: the whole result of a runbook run as
// one card — verdict line, the 7-day same-hour pattern strip (green tick for
// a clean day, a sparkline only where something spiked), every metric that
// moved with the lead (each with its own small chart against it), the checks
// that stayed normal collapsed by group, and the visibility-gaps row.
//
// Every word and number comes from the backend block (docs/runbook-findings-
// block.md); this component owns layout, the fallback ladder and nothing else.
// Shared by the widget (shadow DOM) and the dashboard — hosts load
// `@netwatchai/chat-core/findings.css`.

import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { NwChart } from './NwChart'
import { SeverityIcon } from './Severity'
import { normalizeChartBlock } from './chartSpec'
import {
  clockLabel,
  dayCaption,
  dayKind,
  dayLabel,
  formatCorrelation,
  groupPill,
  normalizeFindings,
  slatePill,
  slateWord,
  sparklinePath,
  sparklineThresholdY,
  type FindingsBlock as FindingsData,
  type FindingsCheck,
  type FindingsDay,
  type FindingsGroup,
  type FindingsMover,
  type FindingsPattern,
  type FindingsProcesses,
  type FindingsSlate,
  type FindingsTable,
} from './findingsSpec'

export type { FindingsData }

interface FindingsBlockProps {
  data: FindingsData | null | undefined
  raw?: string
  /** The turn is still streaming — an unresolved ref is expected, not broken. */
  busy?: boolean
  /** Host-supplied content rendered inside the card frame, after every
   *  data-driven section (the incident detail's diagnosis reading —
   *  remediation, caveats). The block data itself stays deterministic. */
  footer?: ReactNode
}

const RAW_LIMIT = 2048
const SPARK_W = 72
const SPARK_H = 26

export function FindingsBlock({ data, raw, busy, footer }: FindingsBlockProps) {
  const result = useMemo(() => normalizeFindings(data), [data])

  if (!result.ok) {
    if (result.reason === 'findings not loaded') {
      return busy
        ? <div className="nw-fb nw-fb-skel" aria-busy="true"><div className="nw-fb-skel-bar" /><div className="nw-fb-skel-bar short" /></div>
        : <div className="nw-fb"><div className="nw-fb-note">Findings unavailable — ask me to run the check again.</div></div>
    }
    const text = raw ?? ''
    return (
      <div className="nw-fb">
        <div className="nw-fb-note">Couldn’t show these findings. Ask me to run the check again.</div>
        {text.length > 0 && (
          <details className="nw-fb-rawwrap"><summary>Show data</summary><pre className="nw-fb-raw">{text.slice(0, RAW_LIMIT)}</pre></details>
        )}
      </div>
    )
  }

  const { verdict, checked, caption, leadDetail, pattern, movers, normal, slate, look,
          recurring, processes, problems, next, tables } = result.view
  return (
    <section className={`nw-fb nw-fb-${verdict.key}`} aria-label={verdict.title}>
      <header className="nw-fb-verdict">
        <SeverityIcon level={verdict.level} size={18} title={verdict.title} />
        <div className="nw-fb-verdict-text">
          <div className="nw-fb-verdict-title">
            {verdict.title}
            {verdict.state && (
              <span className={`nw-fb-pill nw-fb-state nw-fb-state-${verdict.state}`} title="how settled the diagnosis is">
                {verdict.state}
              </span>
            )}
          </div>
          {caption && <p className="nw-fb-caption">{caption}</p>}
          {verdict.detail && <p className="nw-fb-verdict-detail">{verdict.detail}</p>}
          {leadDetail && <p className="nw-fb-lead">{leadDetail}</p>}
        </div>
        {checked > 0 && (
          <div className="nw-fb-checked" title="metrics checked across the window">
            <span className="nw-fb-checked-n">{checked}</span>
            <span className="nw-fb-checked-l">checked</span>
          </div>
        )}
      </header>

      {pattern && <Pattern pattern={pattern} />}

      {recurring && <Lines title={recurring.title} lines={recurring.lines} />}

      {movers && (
        <div className="nw-fb-sec">
          <h4 className="nw-fb-h">{movers.title}</h4>
          {movers.summary && <p className="nw-fb-p">{movers.summary}</p>}
          {movers.items.map((m) => <Mover key={m.check} mover={m} />)}
        </div>
      )}

      {processes && <Processes section={processes} />}

      {tables.map((t, i) => <Table key={i} table={t} />)}

      {slate && <Slate slate={slate} />}

      {look && (
        <div className="nw-fb-sec nw-fb-look">
          <h4 className="nw-fb-h">{look.title}</h4>
          <p className="nw-fb-p">{look.text}</p>
        </div>
      )}

      {normal && (
        <div className="nw-fb-sec">
          <h4 className="nw-fb-h">{normal.title}</h4>
          {normal.summary && <p className="nw-fb-p">{normal.summary}</p>}
          <div className="nw-fb-groups">
            {normal.groups.map((g) => <Group key={g.key} group={g} windows={Math.max(1, normal.windows?.length ?? 1)} />)}
            {normal.gaps.count > 0 && <Gaps count={normal.gaps.count} labels={normal.gaps.labels} />}
          </div>
        </div>
      )}

      {problems && (
        <div className="nw-fb-sec nw-fb-problems">
          <h4 className="nw-fb-h">{problems.title}</h4>
          <ul className="nw-fb-lines">
            {problems.names.map((n, i) => <li key={i}>{n}</li>)}
            {problems.count > problems.names.length && (
              <li className="nw-fb-lines-more">…and {problems.count - problems.names.length} more</li>
            )}
          </ul>
        </div>
      )}

      {next && (
        <div className="nw-fb-sec nw-fb-next">
          <h4 className="nw-fb-h">{next.title}</h4>
          <ul className="nw-fb-lines">{next.items.map((s, i) => <li key={i}>{s}</li>)}</ul>
        </div>
      )}

      {footer && <div className="nw-fb-sec nw-fb-footer">{footer}</div>}
    </section>
  )
}

// --- markdown-ish bullet lines from the evidence trail (amendment 20) ---

/** `**bold**` and `` `code` `` — all the collectors emit; the markdown
 *  pipeline stays in chat. Exported for hosts composing footer content. */
export function inlineMarks(text: string): ReactNode[] {
  const out: ReactNode[] = []
  const re = /\*\*(.+?)\*\*|`([^`]+)`/g
  let last = 0
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index))
    out.push(m[1] !== undefined ? <b key={out.length}>{m[1]}</b> : <code key={out.length}>{m[2]}</code>)
    last = m.index + m[0].length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

/** Flat "- …" bullets with "  - …" evidence nested under the previous claim. */
function Lines({ title, lines }: { title: string; lines: string[] }) {
  const items: { text: string; subs: string[] }[] = []
  for (const raw of lines) {
    if (typeof raw !== 'string' || !raw.trim()) continue
    const nested = /^\s+-\s/.test(raw)
    const text = raw.replace(/^\s*-\s*/, '').trim()
    if (nested && items.length > 0) items[items.length - 1].subs.push(text)
    else items.push({ text, subs: [] })
  }
  return (
    <div className="nw-fb-sec">
      <h4 className="nw-fb-h">{title}</h4>
      <ul className="nw-fb-lines">
        {items.map((it, i) => (
          <li key={i}>
            {inlineMarks(it.text)}
            {it.subs.length > 0 && (
              <ul className="nw-fb-lines">{it.subs.map((s, j) => <li key={j}>{inlineMarks(s)}</li>)}</ul>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

// --- pattern strip ---

function Pattern({ pattern }: { pattern: FindingsPattern }) {
  return (
    <div className="nw-fb-sec nw-fb-pattern">
      <h4 className="nw-fb-h">{pattern.title}</h4>
      {pattern.subtitle && <p className="nw-fb-p nw-fb-sub">{pattern.subtitle}</p>}
      <div className={`nw-fb-banner nw-fb-banner-${pattern.tone}`}>{pattern.summary}</div>
      <div className="nw-fb-days" role="list">
        {pattern.days.map((d) => <Day key={d.start} day={d} threshold={pattern.threshold} unit={pattern.unit} />)}
      </div>
    </div>
  )
}

function Day({ day, threshold, unit }: { day: FindingsDay; threshold: number | null; unit: string }) {
  const kind = dayKind(day)
  const caption = dayCaption(day)
  return (
    <div className={`nw-fb-day nw-fb-day-${day.outcome}`} role="listitem" aria-label={`${dayLabel(day)}: ${caption}`}>
      <div className="nw-fb-day-label">{dayLabel(day)}</div>
      <div className="nw-fb-day-body">
        {kind === 'tick' && <SeverityIcon level="ok" size={18} title="clean" />}
        {kind === 'spark' && day.points && (
          <svg className="nw-fb-spark" viewBox={`0 0 ${SPARK_W} ${SPARK_H}`} width={SPARK_W} height={SPARK_H} aria-hidden="true">
            {(() => {
              const ty = sparklineThresholdY(day.points, SPARK_H, threshold)
              return ty == null ? null : <line className="nw-fb-spark-thr" x1={0} x2={SPARK_W} y1={ty} y2={ty} />
            })()}
            <path className="nw-fb-spark-line" d={sparklinePath(day.points, SPARK_W, SPARK_H, threshold)} />
          </svg>
        )}
        {kind === 'peak' && (
          <div className="nw-fb-day-peak" title="only hourly trends remain for this day">
            {day.peak != null ? `${day.peak}${unit}` : '—'}
          </div>
        )}
        {kind === 'none' && <div className="nw-fb-day-none">—</div>}
      </div>
      <div className="nw-fb-day-cap">{caption}</div>
    </div>
  )
}

// --- movers ---

function Mover({ mover }: { mover: FindingsMover }) {
  const chart = useMemo(() => (mover.chart ? normalizeChartBlock(mover.chart, undefined) : null), [mover.chart])
  return (
    <div className={`nw-fb-mover nw-fb-mover-${mover.kind}`}>
      <div className="nw-fb-mover-row">
        <span className="nw-fb-mover-label">{mover.label}</span>
        <span className="nw-fb-mover-val">
          {mover.value_text}
          {mover.baseline_text && <span className="nw-fb-mover-base"> vs {mover.baseline_text}</span>}
        </span>
        <span className="nw-fb-mover-corr" title="correlation with the lead metric over the window">
          {formatCorrelation(mover.correlation)}
        </span>
        <span className={`nw-fb-pill nw-fb-pill-${mover.kind === 'in_band' ? 'inband' : mover.severity === 'strong' ? 'strong' : 'warn'}`}>
          {mover.kind === 'in_band' ? 'within range' : mover.severity === 'strong' ? 'strong' : 'deviating'}
        </span>
      </div>
      {chart && chart.ok && (
        <div className="nw-fb-mover-chart">
          <NwChart
            spec={chart.spec}
            height={140}
            ariaLabel={chart.spec.title}
            inkVar="--nw-text"
            faintVar="--nw-text-tertiary"
            gridVar="--nw-border"
            thresholdVar="--nw-danger"
            surfaceVar="--nw-bg"
          />
        </div>
      )}
    </div>
  )
}

// --- slate: hypotheses the deterministic checks tested ---

function Slate({ slate }: { slate: FindingsSlate }) {
  return (
    <div className="nw-fb-sec nw-fb-slate">
      <h4 className="nw-fb-h">{slate.title}</h4>
      {slate.summary && <p className="nw-fb-p">{slate.summary}</p>}
      <div className="nw-fb-groups">
        {slate.items.map((it) => (
          <div key={it.check} className={`nw-fb-slate-row nw-fb-slate-${it.outcome}`}>
            {/* The resolved finding leads; the tested hypothesis stays as the
                row's tooltip (provenance). Legacy rows have no finding — the
                hypothesis renders as before. */}
            <span className="nw-fb-slate-hyp" title={it.finding ? `Tested: ${it.hypothesis}` : undefined}>
              {it.finding || it.hypothesis}
            </span>
            <span className={`nw-fb-pill nw-fb-pill-${slatePill(it.outcome)}`}>{slateWord(it.outcome)}</span>
            {it.detail && <span className="nw-fb-slate-detail">{it.detail}</span>}
          </div>
        ))}
      </div>
    </div>
  )
}

// --- processes ---

/** Processes measured across the spike windows (docs/process-recurrence.md):
 *  the snapshot nearest the peak as one line, then name · min–max · how many
 *  windows (or, on a single-window basis, the share of samples). Blocks
 *  without rows keep the collector's snapshot lines. */
function Processes({ section }: { section: FindingsProcesses }) {
  if (!section.rows.length) return <Lines title={section.title} lines={section.lines} />
  const multi = section.basis === 'spike_windows'
  const peak = section.peak
  const peakClock = peak ? (peak.at != null ? clockLabel(peak.at) : peak.label) : ''
  return (
    <div className="nw-fb-sec">
      <h4 className="nw-fb-h">{section.title}</h4>
      {peak && <p className="nw-fb-p">At the peak{peakClock ? ` (${peakClock})` : ''}: {peak.text}</p>}
      <table className="nw-fb-checks nw-fb-procs">
        <thead>
          <tr className="nw-fb-checks-head">
            <th scope="col" />
            <th scope="col" className="nw-fb-check-val">min – max</th>
            <th scope="col" className="nw-fb-proc-win">{multi ? 'windows' : 'samples'}</th>
          </tr>
        </thead>
        <tbody>
          {section.rows.map((r) => (
            <tr key={r.name}>
              <td className="nw-fb-check-label nw-fb-proc-name">{r.name}</td>
              <td className="nw-fb-check-val">{r.range_text}</td>
              <td className="nw-fb-proc-win" title={multi ? 'spike windows it was listed in' : 'share of samples it was listed in'}>
                {multi
                  ? `${r.windows} of ${r.windows_total}`
                  : typeof r.presence === 'number' ? `${Math.round(r.presence * 100)}%` : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {section.note && <p className="nw-fb-note-small">{section.note}</p>}
    </div>
  )
}

// --- titled grids (docs/findings-everywhere.md) ---

/** One producer table: title, column heads, formatted string cells. A row's
 *  `tone` colours its leading cell (muted greys the row); `indent` nests it
 *  under the previous row. Wide grids scroll inside the card, never the page. */
function Table({ table }: { table: FindingsTable }) {
  return (
    <div className="nw-fb-sec nw-fb-tablesec">
      <h4 className="nw-fb-h">{table.title}</h4>
      {table.rows.length === 0 ? (
        <p className="nw-fb-p">{table.empty ?? 'Nothing to show.'}</p>
      ) : (
        <div className="nw-fb-tablewrap">
          <table className="nw-fb-checks nw-fb-table">
            <thead>
              <tr className="nw-fb-checks-head">
                {table.columns.map((c) => (
                  <th key={c.key} scope="col" className={c.align === 'right' ? 'nw-fb-cell-right' : 'nw-fb-cell-left'}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((r, i) => (
                <tr key={i} className={`${r.tone ? `nw-fb-row-${r.tone}` : ''}${r.indent ? ' nw-fb-row-indent' : ''}`.trim() || undefined}>
                  {table.columns.map((c, j) => (
                    <td key={c.key} className={`${c.align === 'right' ? 'nw-fb-cell-right' : 'nw-fb-cell-left'}${j === 0 ? ' nw-fb-cell-lead' : ''}`}>
                      {r[c.key] as string}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {table.note && <p className="nw-fb-note-small">{table.note}</p>}
    </div>
  )
}

// --- normal groups ---

function Group({ group, windows }: { group: FindingsGroup; windows: number }) {
  const [open, setOpen] = useState(false)
  const n = group.count
  // Ranges exist only on blocks built after the spike-window sampling landed;
  // older stored blocks fall back to today's value column. The row is the
  // metric and its min–max only: the baseline / vs-7d / corr cells came out
  // of the card in the incident-investigation refactor (the movers section
  // above already tells the deviation story; the table is the range story).
  const ranged = group.checks.some((c) => c.range_text)
  return (
    <div className={`nw-fb-group${open ? ' open' : ''}`}>
      <button type="button" className="nw-fb-group-row" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span className="nw-fb-group-caret" aria-hidden="true">▸</span>
        <span className="nw-fb-group-label">{group.label}</span>
        <span className="nw-fb-group-count">{n} metric{n === 1 ? '' : 's'}</span>
        <span className={`nw-fb-pill nw-fb-pill-${groupPill(group.outcome).cls}`}>{groupPill(group.outcome).word}</span>
      </button>
      {open && (
        <table className="nw-fb-checks">
          {ranged && (
            <thead>
              <tr className="nw-fb-checks-head">
                <th scope="col" />
                <th scope="col" className="nw-fb-check-val">min – max in window{windows > 1 ? 's' : ''}</th>
              </tr>
            </thead>
          )}
          <tbody>
            {group.checks.map((c) => (
              <tr key={c.label} className={c.outcome === 'elevated' ? 'nw-fb-check-elevated' : undefined}>
                <td className="nw-fb-check-label">{c.label}{c.outcome === 'elevated' && <span className="nw-fb-check-tag">high, usual</span>}</td>
                <td className="nw-fb-check-val" title={rangeTitle(c, windows)}>
                  {c.range_text || c.value_text}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function rangeTitle(c: FindingsCheck, windows: number): string | undefined {
  if (!c.range_text) return undefined
  const k = c.windows ?? 1
  if (windows > 1 && k < windows) return `min – max across ${k} of the ${windows} windows (raw history missing on the rest); today's value ${c.value_text}`
  return windows > 1 ? `min – max across the ${windows} spike windows; today's value ${c.value_text}` : `min – max inside this window; average ${c.value_text}`
}

function Gaps({ count, labels }: { count: number; labels: string[] }) {
  const [open, setOpen] = useState(false)
  return (
    <div className={`nw-fb-group nw-fb-gaps${open ? ' open' : ''}`}>
      <button type="button" className="nw-fb-group-row" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <span className="nw-fb-group-caret" aria-hidden="true">▸</span>
        <span className="nw-fb-group-label">Not collected</span>
        <span className="nw-fb-group-count">{count} check{count === 1 ? '' : 's'}</span>
        <span className="nw-fb-pill nw-fb-pill-muted">no data</span>
      </button>
      {open && <div className="nw-fb-gaps-list">{labels.join(' · ')}</div>}
    </div>
  )
}
